import { toast } from "react-toastify";
import {
  CalendarEvent,
  CalendarEventDetails,
  CalendarEventCache,
  CalendarEventsCache,
  CalendarSchedule,
  CalendarScheduleQuery,
  MyProfile,
  MyProfileCache,
  CalendarScheduleCache,
} from "@/api/calendarTypes";
import { MailMessage, MailMessagesCache } from "@/api/mailTypes";
import { dateToYYYYMMDD } from "@/utils/date";

// Enkel minne-cache for kalender-events
const calendarEventsCache: CalendarEventsCache = {};
const calendarEventCache: CalendarEventCache = {};
const myProfileCache: MyProfileCache = {};
const calendarScheduleCache: CalendarScheduleCache = {};
const emailsCache: MailMessagesCache = {};

// Cache expiry tracking (timestamp in ms)
const cacheExpiry: Record<string, number> = {};
const CACHE_TTL_MS = 30 * 1000; // 30 seconds

// Pending requests map to prevent race conditions
const pendingScheduleRequests: Map<
  string,
  Promise<CalendarScheduleQuery>
> = new Map();

async function fetchEvent(
  url: string,
  token: string,
  signal: AbortSignal
): Promise<CalendarEventDetails> {
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      Prefer: 'outlook.timezone="Europe/Oslo"',
    },
    signal,
  });
  if (!res.ok) {
    throw new Error(`Graph API error: ${res.status} ${res.statusText}`);
  }
  return await res.json();
}

async function fetchAllEvents(
  url: string,
  token: string
): Promise<CalendarEvent[]> {
  let allEvents: CalendarEvent[] = [];
  let nextUrl: string | null = url;

  while (nextUrl) {
    const res = await fetch(nextUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        Prefer: 'outlook.timezone="Europe/Oslo"',
      },
    });

    if (!res.ok) {
      throw new Error(`Graph API error: ${res.status} ${res.statusText}`);
    }

    const data: { value?: CalendarEvent[]; "@odata.nextLink"?: string } =
      await res.json();

    if (data.value) {
      allEvents = allEvents.concat(data.value);
    }

    nextUrl = data["@odata.nextLink"] ?? null;
  }

  return allEvents;
}

export async function getCalendarEvents(
  token: string,
  startDate: string | Date,
  endDate: string | Date,
  refetch = false
): Promise<CalendarEvent[]> {
  const start =
    typeof startDate === "string" ? startDate : dateToYYYYMMDD(startDate);
  const end = typeof endDate === "string" ? endDate : dateToYYYYMMDD(endDate);
  const cacheKey = `${token}_${start}_${end}`;

  if (calendarEventsCache[cacheKey] && !refetch) {
    return calendarEventsCache[cacheKey];
  }

  const baseUrl =
    `https://graph.microsoft.com/v1.0/me/calendarview` +
    `?startdatetime=${start}T00:00:00.000Z` +
    `&enddatetime=${end}T23:59:59.999Z` +
    `&$orderby=start/dateTime` +
    `&$select=id,subject,start,end,location,bodyPreview,organizer,attendees`;

  const result = await fetchAllEvents(baseUrl, token);

  calendarEventsCache[cacheKey] = result;
  return result;
}

export async function getCalendarEventById(
  token: string,
  eventId: string,
  signal: AbortSignal
): Promise<CalendarEventDetails> {
  const cacheKey = `${token}_event_${eventId}`;
  const cachedEvent = calendarEventCache[cacheKey];
  if (cachedEvent) {
    return cachedEvent;
  }

  const url = `https://graph.microsoft.com/v1.0/me/events/${eventId}`;
  const result = await fetchEvent(url, token, signal);
  calendarEventCache[cacheKey] = result;
  return result;
}

export async function createEvent(
  token: string,
  event: CalendarEventDetails
): Promise<CalendarEventDetails> {
  const response = await fetch("https://graph.microsoft.com/v1.0/me/events", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(event),
  });

  if (!response.ok) {
    const errorText = await response.text();
    toast.error(
      `Failed to create event: ${response.status} ${response.statusText} - ${errorText}`
    );
  }

  return response.json();
}

// Re-export updateCalendarEvent from separate file
export { updateCalendarEvent } from "./graphServices/updateCalendarEvent";

export async function getSchedule(
  token: string,
  attendees: string[],
  startDate: string | Date,
  endDate: string | Date,
  interval = 30,
  skipCache = false
): Promise<CalendarScheduleQuery> {
  if (attendees.length === 0) {
    return { value: [] };
  }

  const start =
    typeof startDate === "string" ? startDate : dateToYYYYMMDD(startDate);
  const end = typeof endDate === "string" ? endDate : dateToYYYYMMDD(endDate);

  // Sort attendees to ensure consistent cache key regardless of order
  const sortedAttendees = [...attendees].sort();
  const cacheKey = `schedule_${sortedAttendees.join(",")}_${start}_${end}`;

  // Check cache first and verify it hasn't expired
  const now = Date.now();
  if (
    !skipCache &&
    calendarScheduleCache[cacheKey] &&
    cacheExpiry[cacheKey] &&
    cacheExpiry[cacheKey] > now
  ) {
    return calendarScheduleCache[cacheKey];
  }

  // Check if there's already a pending request for this exact query
  if (pendingScheduleRequests.has(cacheKey)) {
    // Wait for the existing request to complete
    return await pendingScheduleRequests.get(cacheKey)!;
  }

  // Create new request
  const requestPromise = (async () => {
    try {
      const url = "https://graph.microsoft.com/v1.0/me/calendar/getSchedule";
      const body = {
        schedules: sortedAttendees,
        startTime: {
          dateTime: `${start}T00:00:00`,
          timeZone: "Europe/Oslo",
        },
        endTime: {
          dateTime: `${end}T23:59:59`,
          timeZone: "Europe/Oslo",
        },
        availabilityViewInterval: interval,
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorText = await response.text();
        toast.error(
          `Failed to get schedule: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      const result = await response.json();
      calendarScheduleCache[cacheKey] = result;
      cacheExpiry[cacheKey] = Date.now() + CACHE_TTL_MS;
      return result;
    } finally {
      // Clean up pending request after completion
      pendingScheduleRequests.delete(cacheKey);
    }
  })();

  // Store pending request
  pendingScheduleRequests.set(cacheKey, requestPromise);

  return await requestPromise;
}

export async function getMyProfile(
  token: string,
  signal?: AbortSignal
): Promise<MyProfile> {
  const today = dateToYYYYMMDD(new Date());
  const cacheKey = `${token}_${today}`;
  if (myProfileCache[cacheKey]) {
    return myProfileCache[cacheKey];
  }

  const url = "https://graph.microsoft.com/v1.0/me";
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    toast.error(
      `Failed to fetch profile: ${response.status} ${response.statusText} - ${errorText}`
    );
    throw new Error(
      `Graph API error: ${response.status} ${response.statusText}`
    );
  }

  const profile = await response.json();
  myProfileCache[cacheKey] = profile;
  return profile;
}
type StreamOptions = {
  useCacheOnly?: boolean; // hvis true → strøm KUN cache (eller ingenting)
  signal?: AbortSignal; // for avbryt
  select?: string[]; // felter å hente via $select
  pageSize?: number; // 1–999 (Graph praksis)
  maxItems?: number; // totalt tak på antall meldinger
  mailFolderId?: string; // begrens til mappe
};

const DEFAULT_SELECT =
  "id,subject,from,toRecipients,receivedDateTime,bodyPreview,webLink,sender";

// ===== Public: strømmer EN-OG-EN melding =====
export async function* getEmailsFromOrToStream(
  token: string,
  emailAddress: string,
  options?: StreamOptions
): AsyncGenerator<MailMessage, void, unknown> {
  const batched = getEmailsFromOrToBatched(token, emailAddress, options);
  for await (const page of batched) {
    for (const msg of page) {
      yield msg;
    }
  }
}

// ===== Public: strømmer BATCHE(R) med meldinger =====
export async function* getEmailsFromOrToBatched(
  token: string,
  emailAddress?: string,
  options?: StreamOptions & { dateFrom?: string | Date }
): AsyncGenerator<MailMessage[], void, unknown> {
  if (!emailAddress) {
    return; // ingen epostadresse
  }
  const today = dateToYYYYMMDD(new Date());
  const dateFromStr = options?.dateFrom
    ? typeof options.dateFrom === "string"
      ? options.dateFrom
      : dateToYYYYMMDD(options.dateFrom)
    : "";
  const cacheKey = [
    token,
    today,
    emailAddress.toLowerCase(),
    (options?.select ?? []).sort().join(","),
    options?.pageSize ?? 100,
    options?.maxItems ?? "all",
    options?.mailFolderId ?? "all",
    dateFromStr,
    "from+to_stream",
  ].join("|");

  // 1) Strøm cache hvis tilgjengelig
  if (emailsCache[cacheKey]?.length) {
    yield emailsCache[cacheKey];
    return; // ferdig dersom vi har cache
  }
  if (options?.useCacheOnly) {
    return; // ingen nettkall
  }

  const select = options?.select?.length
    ? options.select.join(",")
    : DEFAULT_SELECT;
  const pageSize = clamp(options?.pageSize ?? 100, 1, 999);
  const maxItems = options?.maxItems ?? Number.POSITIVE_INFINITY;

  const basePath = options?.mailFolderId
    ? `https://graph.microsoft.com/v1.0/me/mailFolders/${options.mailFolderId}/messages`
    : `https://graph.microsoft.com/v1.0/me/messages`;

  // FRA (støttet i $filter). NB: uten $orderby for å unngå InefficientFilter
  let filter = `from/emailAddress/address eq '${escapeODataLiteral(
    emailAddress
  )}'`;
  if (dateFromStr) {
    const dateLiteral = `${dateFromStr}T00:00:00Z`; // DateTimeOffset literal — no quotes
    filter += ` and receivedDateTime ge ${dateLiteral}`;
  }

  const filterUrl =
    `${basePath}` +
    `?$filter=${encodeURIComponent(filter)}` +
    `&$top=${pageSize}` +
    `&$select=${encodeURIComponent(select)}`;

  // TIL/CC/BCC via $search (uten $orderby).
  const kql = `to:${emailAddress} OR cc:${emailAddress} OR bcc:${emailAddress}`;
  // $search expects the query in quotes; encode the whole value (including the quotes)
  let searchUrl =
    `${basePath}` +
    `?$search=${encodeURIComponent(`"${kql}"`)}` +
    `&$top=${pageSize}` +
    `&$select=${encodeURIComponent(select)}`;

  if (dateFromStr) {
    const dateLiteral = `${dateFromStr}T00:00:00Z`; // no quotes
    searchUrl += `&$filter=${encodeURIComponent(
      `receivedDateTime ge ${dateLiteral}`
    )}`;
  }

  const dedupe = new Set<string>();
  const collectedForCache: MailMessage[] = [];

  // Hent begge “strømmer” parallelt, og yield fortløpende etter hvert som sidene kommer
  const producers = [
    pageIterator(filterUrl, token, {
      signal: options?.signal,
      useSearch: false,
      maxItems,
    }),
    pageIterator(searchUrl, token, {
      signal: options?.signal,
      useSearch: true,
      maxItems,
    }),
  ];

  // Runde-robin over to async generators:
  let active = producers.length;
  const nexts = producers.map((p) => p.next());

  while (active > 0) {
    const idx = await Promise.race(
      nexts.map((p, i) => p.then((res) => ({ i, res })))
    );

    const { i, res } = idx;
    if (res.done) {
      active--;
      // erstatt med en "hengende" promise som aldri vinner race
      nexts[i] = new Promise<IteratorResult<MailMessage[]>>(() => {
        // Intentionally never resolves to remove this producer from race
      });
      continue;
    }

    // res.value er en batch (page) med meldinger
    const page = res.value.filter((m) => {
      if (!m?.id) return false;
      if (dedupe.has(m.id)) return false;
      dedupe.add(m.id);
      return true;
    });

    if (page.length) {
      // legg til i cache-buffer
      collectedForCache.push(...page);

      // sorter batch lokalt (nyeste først) for bedre UX
      page.sort(
        (a, b) => dateNum(b.receivedDateTime) - dateNum(a.receivedDateTime)
      );

      yield page;
    }

    // be om neste side fra samme producer
    nexts[i] = producers[i].next();
  }

  // Ferdig: lagre i cache (hele settet)
  if (collectedForCache.length) {
    // Stabil slutt-sortering
    collectedForCache.sort(
      (a, b) => dateNum(b.receivedDateTime) - dateNum(a.receivedDateTime)
    );
    emailsCache[cacheKey] = collectedForCache;
  }
}

// ===== Intern: iterator som henter side for side =====
async function* pageIterator(
  startUrl: string,
  token: string,
  opts: { signal?: AbortSignal; useSearch?: boolean; maxItems: number }
): AsyncGenerator<MailMessage[], void, unknown> {
  let url: string | null = startUrl;
  let total = 0;
  let pages = 0;

  while (url && total < opts.maxItems) {
    const response = await fetchWithBackoff(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...(opts.useSearch ? { ConsistencyLevel: "eventual" } : {}),
      },
      signal: opts.signal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      toast.error(
        `Failed to fetch emails: ${response.status} ${response.statusText} - ${errorText}`
      );
      throw new Error(
        `Graph API error: ${response.status} ${response.statusText}`
      );
    }

    const page = (await response.json()) as {
      value: MailMessage[];
      "@odata.nextLink"?: string;
    };
    const items = page.value ?? [];
    total += items.length;

    yield items;

    url = page["@odata.nextLink"] ?? null;
    if (++pages > 500) break; // failsafe
  }
}

// ===== Intern: enkel backoff for 429/503 =====
async function fetchWithBackoff(
  url: string,
  init: RequestInit,
  retries = 4
): Promise<Response> {
  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await fetch(url, init);
    if (res.status !== 429 && res.status !== 503) return res;

    if (attempt >= retries) return res;

    const retryAfter = parseInt(res.headers.get("Retry-After") ?? "", 10);
    const ms = Number.isFinite(retryAfter)
      ? retryAfter * 1000
      : Math.min(16000, 1000 * Math.pow(2, attempt));
    await sleep(ms);
    attempt++;
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const clamp = (n: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, n));
const dateNum = (iso?: string) => (iso ? Date.parse(iso) : 0);
const escapeODataLiteral = (s: string) => s.replace(/'/g, "''");
